import React, { useState, useEffect } from "react";
import { API_URL } from "../config";
import "./CuppingsList.css";

const CuppingsList = ({ refreshTrigger }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCupping, setSelectedCupping] = useState(null);

  useEffect(() => {
    fetchBatches();
  }, [refreshTrigger]);

  const fetchBatches = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/batches?include_cuppings=true`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch batches");
      }

      const data = await response.json();
      setBatches(data);
    } catch (err) {
      setError(err.message);
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCuppingDetails = async (cuppingId) => {
    try {
      const response = await fetch(`${API_URL}/cuppings/${cuppingId}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch cupping details");
      }

      const data = await response.json();
      setSelectedCupping(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteCupping = async (cuppingId) => {
    if (!window.confirm("Are you sure you want to delete this cupping?")) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/cuppings/${cuppingId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to delete cupping");
      }

      // Remove cupping from nested batches state
      setBatches((prev) =>
        prev.map((b) => ({
          ...b,
          cuppings: (b.cuppings || []).filter((c) => c.id !== cuppingId),
        }))
      );
      setSelectedCupping(null);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="cuppings-list">
      <div className="list-header">
        <h2>Cupping Records</h2>
        <button onClick={fetchBatches} className="btn-refresh">
          Refresh
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading cuppings...</div>
      ) : batches.length === 0 ? (
        <div className="empty-state">
          <p>No cuppings yet. Record your first audio note above.</p>
        </div>
      ) : (
        <div className="batches-list">
          {batches.map((batch) => (
            <div key={batch.id} className="batch-block">
              <div className="batch-header">
                {/* INLINE STYLES REMOVED */}
                <h3>{batch.batch_label}</h3>
                <p className="batch-id-small">ID: {batch.id.slice(0, 8)}...</p>
              </div>

              <div className="batch-cuppings">
                {(!batch.cuppings || batch.cuppings.length === 0) && (
                  <div className="no-cuppings">No cuppings for this batch.</div>
                )}

                <div className="cuppings-grid">
                  {(batch.cuppings || []).map((cupping) => (
                    <div key={cupping.id} className="cupping-card">
                      <div className="card-header">
                        {/* Using cupping-specific title or label */}
                        <h4>{batch.batch_label}</h4>
                        <span className="date">
                          {new Date(cupping.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="preview">
                        {cupping.preview || "No preview available"}
                      </p>
                      <div className="card-actions">
                        <button
                          onClick={() => getCuppingDetails(cupping.id)}
                          className="btn-view"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => deleteCupping(cupping.id)}
                          className="btn-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedCupping && (
        <div className="cupping-detail-modal">
          <div
            className="modal-overlay"
            onClick={() => setSelectedCupping(null)}
          />
          <div className="modal-content">
            <button
              className="modal-close"
              onClick={() => setSelectedCupping(null)}
            >
              ✕
            </button>

            {/* Header Area */}
            <div className="modal-header">
              <h2>{selectedCupping.batch_label}</h2>
              <p className="batch-id-small">
                Batch ID: {selectedCupping.batch_id}
              </p>
            </div>

            <div className="detail-section">
              <h3>Cupping Notes</h3>
              <div className="notes-display">
                {Object.entries(selectedCupping.cupping_notes || {}).map(
                  ([key, value]) => (
                    <div key={key} className="note-field">
                      <strong>{key}</strong>
                      <span>
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>

            <div className="detail-section">
              <h3>Entries ({selectedCupping.entries.length})</h3>
              <div className="entries-list">
                {selectedCupping.entries.map((entry) => (
                  <div key={entry.id} className="entry-item">
                    <div className="entry-time">
                      {new Date(entry.created_at).toLocaleString()}
                    </div>
                    <p className="entry-transcript">{entry.transcript}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuppingsList;
